import { Row, Col, Button, Accordion, Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faDownload } from '@fortawesome/free-solid-svg-icons';
import Container from '../../../components/ui/Container/Container';
import LoadingContainer from '../../../components/ui/LoadingContainer/LoadingContainer';
import ModalDialog from '../../../components/ui/ModalDialog/ModalDialog';
import { useManageInvoices, UseManageInvoicesReturn } from './useManageInvoices';
import { LateFeeStatus } from '../../../utils/constants/enums';
import styles from './ManageInvoices.module.css';

export const ManageInvoices = () => {
  const {
    metrics,
    loading,
    error,
    showGenerateAnnualInvoicesModal,
    openGenerateAnnualInvoicesModal,
    closeGenerateAnnualInvoicesModal,
    generateAnnualInvoices,
    showGenerateNewCandidateInvoicesModal,
    openGenerateNewCandidateInvoicesModal,
    closeGenerateNewCandidateInvoicesModal,
    generateNewCandidateInvoices,
    showGenerateMissingInvoicesModal,
    openGenerateMissingInvoicesModal,
    closeGenerateMissingInvoicesModal,
    generateMissingInvoices,
    showApplyLateFeesModal,
    openApplyLateFeesModal,
    closeApplyLateFeesModal,
    applyLateFees,
    exportNewCandidateInvoices,
    exportMissingInvoices,
    isGenerating,
    generatingMessage,
    ToastContainer
  }: UseManageInvoicesReturn = useManageInvoices();

  if (loading) {
    return (
      <Container className={styles.pageContainer}>
        <LoadingContainer />
      </Container>
    );
  }

  if (error || !metrics) {
    return (
      <Container className={styles.pageContainer}>
        <div className={styles.statusSection}>
          <div className={styles.statusText}>
            <span className={styles.statusError}>{error || 'Failed to load invoice metrics'}</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <ToastContainer />

      {/* Processing Overlay */}
      {isGenerating && (
        <div className={styles.processingOverlay}>
          <div className={styles.processingContent}>
            <Spinner animation="border" variant="primary" className={styles.processingSpinner} />
            <div className={styles.processingMessage}>{generatingMessage}</div>
          </div>
        </div>
      )}

      <ModalDialog
        showDialog={showGenerateAnnualInvoicesModal}
        heading="Generate Annual Invoices"
        message={`NOTICE: This will overwrite invoice records with missing payments for ${metrics?.billingYear ?? new Date().getFullYear()}. Are you sure you want to continue?`}
        okCallback={generateAnnualInvoices}
        okButtonText="OK"
        cancelCallback={closeGenerateAnnualInvoicesModal}
        cancelButtonText="Cancel"
      />
      <ModalDialog
        showDialog={showGenerateNewCandidateInvoicesModal}
        heading="Generate New Candidate Invoices"
        message={`NOTICE: This will generate invoice records for ${metrics?.newCandidateCount ?? 0} 'New Candidate' membership${(metrics?.newCandidateCount ?? 0) === 1 ? '' : 's'}. Are you sure you want to continue?`}
        okCallback={generateNewCandidateInvoices}
        okButtonText="OK"
        cancelCallback={closeGenerateNewCandidateInvoicesModal}
        cancelButtonText="Cancel"
      />
      <ModalDialog
        showDialog={showGenerateMissingInvoicesModal}
        heading="Generate Missing Invoices"
        message={`NOTICE: This will generate missing invoice records for ${metrics?.missingInvoiceCount ?? 0} membership${(metrics?.missingInvoiceCount ?? 0) === 1 ? '' : 's'}. Are you sure you want to continue?`}
        okCallback={generateMissingInvoices}
        okButtonText="OK"
        cancelCallback={closeGenerateMissingInvoicesModal}
        cancelButtonText="Cancel"
      />
      <ModalDialog
        showDialog={showApplyLateFeesModal}
        heading="Apply Late Fees"
        message={`NOTICE: This will apply a $${metrics?.lateFeeAmount?.toFixed(2) ?? '0.00'} late fee to ${metrics?.lateFeeNotAppliedCount ?? 0} invoice${(metrics?.lateFeeNotAppliedCount ?? 0) === 1 ? '' : 's'}.\n\nLate fees are only applied to Active or Suspended members whose first joined date is prior to the current billing year and whose outstanding balance (excluding any existing late fee) is greater than $50.00.\n\nAre you sure you want to continue?`}
        okCallback={applyLateFees}
        okButtonText="OK"
        cancelCallback={closeApplyLateFeesModal}
        cancelButtonText="Cancel"
      />
      <Container className={styles.pageContainer}>
        <div className={styles.sectionsContainer}>
          {/* Section 1: Annual Generate Invoices */}
          <Card className={styles.sectionCard}>
            <Card.Header className={styles.sectionHeader}>
              <h4 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
                1. Annual Generate Invoices
              </h4>
            </Card.Header>
            <Card.Body>
              <Accordion className="mb-3">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Instructions:</Accordion.Header>
                  <Accordion.Body>
                    <div className={styles.instructionText}>
                      <p>
                        This full invoice generation process is to be run{' '}
                        <strong>once per year</strong>, usually on December 1st, to create invoice
                        records for new year&apos;s billing and online payments.
                      </p>
                      <p>
                        For each <strong>active</strong> (and suspended) membership the dues will be
                        calculated based on membership type and total volunteer hours worked the
                        prior year. Note that suspended members must continue to pay their dues or
                        lose their membership.
                      </p>
                      <p>
                        These invoice records are required to generate the Dues Notice letters and
                        to facilitate online payments.
                      </p>
                      <ol>
                        <li>
                          Update the dues amount for each Membership Type record to reflect the
                          current fees schedule (System Utilities → Member Types).
                        </li>
                        <li>
                          Update records in the ds_discount_schedule table to define the dues
                          discounts for volunteer work hours.
                        </li>
                        <li>Input Work Hours for all club members prior to generating invoices.</li>
                        <li>
                          Update the member type for anyone who is now eligible for Senior or Life
                          membership (see monthly report).
                        </li>
                        <li>
                          Enter the Billing Year and click the &apos;Generate Invoices&apos; button.
                        </li>
                      </ol>
                      <p>
                        <strong>Notes:</strong>
                      </p>
                      <ul>
                        <li>
                          In the event that we failed to update the dues amount for a membership
                          type prior to running this process, the process may be run a second time.
                          If this is done, all invoice records will be overwritten with new values.
                        </li>
                        <li>
                          Once the first payment has been made for the year this process must not be
                          run again until next year. If it is, any values manually entered into an
                          invoice will be lost.
                        </li>
                      </ul>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className={styles.statusSection}>
                <div className={styles.statusText}>
                  {metrics.invoicesGenerated ? (
                    <span className={styles.statusSuccess}>
                      Invoices have already been generated for {metrics.billingYear}.
                    </span>
                  ) : (
                    <span className={styles.statusWarning}>
                      Invoices have not been generated for {metrics.billingYear}.
                    </span>
                  )}
                  <br />
                  <span>
                    There are currently {metrics.activeMembershipCount} active (active, new and
                    suspended) invoices.
                  </span>
                </div>
              </div>

              <Row className="mt-3">
                <Col md={3}>
                  <label className={styles.inputLabel}>For Billing Year:</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    defaultValue={metrics.billingYear.toString()}
                    readOnly
                  />
                </Col>
              </Row>

              <div className={styles.actionButtons}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={openGenerateAnnualInvoicesModal}
                  disabled={isGenerating}>
                  Generate Annual Invoices
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Section 2: Generate New Candidate Invoices */}
          <Card className={styles.sectionCard}>
            <Card.Header className={styles.sectionHeader}>
              <h4 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
                2. Generate <u>New Candidate</u> Invoices
              </h4>
            </Card.Header>
            <Card.Body>
              <Accordion className="mb-3">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Instructions:</Accordion.Header>
                  <Accordion.Body>
                    <div className={styles.instructionText}>
                      <p>
                        An invoice will be generated for all members at the beginning of the billing
                        year, in December. This section is used to create invoices for new members.
                      </p>
                      <p>
                        For new members joining in November make sure to wait until November 1st to
                        generate their invoice records because these memberships are for the new
                        billing year. Also be sure to apply the latest dues values (dollar amount)
                        to Membership Types records before generating any invoices for the new
                        billing year.
                      </p>
                      <ol>
                        <li>
                          Click the &apos;Export New Candidate Invoice List&apos; button if you want
                          to generate a CSV file listing all the &apos;New Candidate&apos;
                          memberships for which we do not have an invoice for the current billing
                          year.
                        </li>
                        <li>
                          Click the &apos;Generate New Candidate Invoices&apos; button to generate
                          an invoice record for any/all &apos;New Candidate&apos; memberships which
                          do not have an invoice for the current billing year.
                        </li>
                      </ol>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className={styles.statusSection}>
                <div className={styles.statusText}>
                  {metrics.newCandidateCount > 0 ? (
                    <span className={styles.statusError}>
                      There are {metrics.newCandidateCount} New Candidate memberships for which we
                      do not have a {metrics.billingYear} invoice record.
                    </span>
                  ) : (
                    <span className={styles.statusSuccess}>
                      All New Candidate memberships have {metrics.billingYear} invoice records.
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.actionButtons}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={exportNewCandidateInvoices}
                  disabled={isGenerating}>
                  <FontAwesomeIcon icon={faDownload} className="me-1" />
                  Export New Candidate Invoice List
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={openGenerateNewCandidateInvoicesModal}
                  disabled={isGenerating}>
                  Generate New Candidate Invoices
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Section 3: Generate Missing Invoices */}
          <Card className={styles.sectionCard}>
            <Card.Header className={styles.sectionHeader}>
              <h4 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
                3. Generate Missing Invoices
              </h4>
            </Card.Header>
            <Card.Body>
              <Accordion className="mb-3">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Instructions:</Accordion.Header>
                  <Accordion.Body>
                    <div className={styles.instructionText}>
                      <p>
                        After running the annual invoice generation, if there are any memberships in
                        the current billing year for which an invoice has not been created, use this
                        section to generate them now.
                      </p>
                      <p>
                        An invoice will be generated for all members at the beginning of the billing
                        year, in December. This section is used to report/create missing invoices
                        due to unusual conditions.
                      </p>
                      <p>
                        For new members joining in November use the &apos;Generate New Candidate
                        Invoices&apos; section to generate only Invoices for New Candidates.
                      </p>
                      <p>
                        The invoice should be used at the time any payment is accepted to ensure the
                        amount paid matches the calculated dues amount (including any discounts or
                        late fees).
                      </p>
                      <p>
                        Also, the invoice record can be used to generate a printed invoice (receipt)
                        for payments made in person, by using the &apos;Print Invoice&apos; button
                        on the Member Detail page Invoices tab.
                      </p>
                      <ol>
                        <li>
                          Click the &apos;Export Missing Invoice List&apos; button if you want to
                          generate a CSV file listing all the memberships for which we do not have
                          an invoice for the current billing year.
                        </li>
                        <li>
                          Click the &apos;Generate Missing Invoices&apos; button to generate an
                          invoice record for any/all active memberships which do not have an invoice
                          for the current billing year.
                        </li>
                      </ol>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className={styles.statusSection}>
                <div className={styles.statusText}>
                  {metrics.missingInvoiceCount > 0 ? (
                    <span className={styles.statusError}>
                      There are {metrics.missingInvoiceCount} memberships for which we do not have a{' '}
                      {metrics.billingYear} invoice record.
                    </span>
                  ) : (
                    <span className={styles.statusSuccess}>
                      All active memberships have {metrics.billingYear} invoice records.
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.actionButtons}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={exportMissingInvoices}
                  disabled={isGenerating}>
                  <FontAwesomeIcon icon={faDownload} className="me-1" />
                  Export Missing Invoice List
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={openGenerateMissingInvoicesModal}
                  disabled={isGenerating}>
                  Generate Missing Invoices
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Section 4: Late Fees */}
          <Card className={styles.sectionCard}>
            <Card.Header className={styles.sectionHeader}>
              <h4 className={styles.sectionTitle}>
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
                4. Late Fees
              </h4>
            </Card.Header>
            <Card.Body>
              <Accordion className="mb-3">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Instructions:</Accordion.Header>
                  <Accordion.Body>
                    <div className={styles.instructionText}>
                      <p>
                        <strong>Dues Period:</strong>
                      </p>
                      <p>
                        The membership secretary will begin accepting annual membership dues for the
                        upcoming new year in January.
                      </p>
                      <p>
                        A member remaining delinquent as of February 15th will be dropped from the
                        Association.
                      </p>
                      <p>
                        During the period from February 16th through March 15th, a dropped member
                        may be reinstated without reapplying to the Association by paying a twenty
                        five dollar late fee.
                      </p>
                      <p>
                        During the period from March 16th through April 15th, a dropped member may
                        be reinstated without reapplying to the Association by paying a fifty dollar
                        late fee.
                      </p>
                      <p>
                        A dropped member wishing to rejoin the Association after April 15th must
                        submit a new application for provisional membership and will forfeit his or
                        her previous membership status.
                      </p>
                      <p>
                        Any person joining the Association after the first of November of any year
                        shall be considered as paying dues for the following year and will be
                        responsible for paying the new member rate of the upcoming year.
                      </p>
                      <p>
                        The Board of Trustees shall be empowered to waive a member&apos;s dues,
                        volunteer work hours, or assessments based on a hardship or a disability.
                        The member must present a written request to the Board of Trustees showing
                        just cause for such a waiver.
                      </p>
                      <ol>
                        <li>
                          When applicable click the &apos;Apply Late Fees&apos; button to write the
                          specified late fee to the invoice records for all active members still
                          showing a balance due for the current billing year. Note that late fees
                          will only be written to invoices for active (and suspended) members, whose
                          first joined date is prior to the current billing year (prior to October
                          31st), and whose balance due is greater than $50.00.
                        </li>
                        <li>
                          After April 15th use the Invoice Report to identify any/all active (and
                          suspended) members who have not paid their dues in full. These members
                          must be dropped by changing their status to &apos;Inactive&apos;.
                        </li>
                      </ol>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className={styles.statusSection}>
                <div className={styles.statusText}>
                  {/* Late Fee Status - matching Java GenInvoiceHelper logic */}
                  {metrics.lateFeeStatus === LateFeeStatus.NoLateFees && (
                    <span className={styles.statusSuccess}>
                      February 15th has not passed yet. No late fees apply.
                    </span>
                  )}

                  {metrics.lateFeeStatus === LateFeeStatus.FirstLateFee && (
                    <>
                      {metrics.lateFeeNotAppliedCount > 0 ? (
                        <span className={styles.statusError}>
                          February 15th has passed.
                          <br />
                          There are {metrics.lateFeeAppliedCount} members for which the $
                          {metrics.lateFeeAmount.toFixed(2)} late fee has been applied and{' '}
                          {metrics.lateFeeNotAppliedCount} members for which the late fee needs to
                          be applied.
                        </span>
                      ) : (
                        <span className={styles.statusSuccess}>
                          February 15th has passed.
                          <br />
                          The ${metrics.lateFeeAmount.toFixed(2)} late fee has already been applied
                          to all the {metrics.lateFeeAppliedCount} members who have not yet paid
                          their dues in full.
                        </span>
                      )}
                    </>
                  )}

                  {metrics.lateFeeStatus === LateFeeStatus.SecondLateFee && (
                    <>
                      {metrics.lateFeeNotAppliedCount > 0 ? (
                        <span className={styles.statusError}>
                          March 15th has passed.
                          <br />
                          There are {metrics.lateFeeAppliedCount} members for which the $
                          {metrics.lateFeeAmount.toFixed(2)} late fee has been applied and{' '}
                          {metrics.lateFeeNotAppliedCount} members for which the late fee needs to
                          be applied.
                        </span>
                      ) : (
                        <span className={styles.statusSuccess}>
                          March 15th has passed.
                          <br />
                          The ${metrics.lateFeeAmount.toFixed(2)} late fee has already been applied
                          to all the {metrics.lateFeeAppliedCount} members who have not yet paid
                          their dues in full.
                        </span>
                      )}
                    </>
                  )}

                  {metrics.lateFeeStatus === LateFeeStatus.DeadlinePassed && (
                    <span className={styles.statusError}>
                      April 15th has passed.
                      <br />
                      There are {metrics.unpaidMemberCount} members who have not paid in full. These
                      members must be dropped now and will have to submit a new application for
                      provisional membership if they wish to rejoin the club (see bylaws).
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.actionButtons}>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={openApplyLateFeesModal}
                  disabled={isGenerating || !metrics.canApplyLateFee}>
                  Apply Late Fees
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  );
};
